// src/features/auth/auth.api.ts
// RTK Query auth endpoints — login, session check (me), and logout.
import { baseApi } from '@/services/base.api'
import { setUser, clearUser } from './auth.slice'
import { syncCsrfToken, setCsrfToken } from '@/services/axios.instance'
import type {
  LoginRequest,
  LoginResponse,
  MeResponse,
  LogoutResponse,
  RefreshResponse,
  ProfileResponse,
  UpdateProfileRequest,
  ForgotPasswordInitiateRequest,
  ForgotPasswordInitiateResponse,
  ForgotPasswordVerifyOtpRequest,
  ForgotPasswordVerifyOtpResponse,
  ForgotPasswordResetRequest,
  ForgotPasswordResetResponse,
} from './auth.types'

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * POST /api/v1/auth/login
     * The backend sets an httpOnly session cookie on success.
     * We only persist the user object in Redux (in-memory).
     */
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        data: credentials,
      }),
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        try {
          const result = await queryFulfilled
          dispatch(setUser(result.data.data))
          // Re-sync CSRF token for the authenticated session (BEST_PRACTICES.md Flow 2)
          syncCsrfToken().catch(() => {})
        } catch {
          // Handled by caller
        }
      },
    }),

    /**
     * GET /api/v1/auth/me  (protected)
     * Called by AppInitializer on every page load to hydrate Redux
     * from the httpOnly session cookie that the browser sends automatically.
     */
    getMe: builder.query<MeResponse, void>({
      query: () => ({
        url: '/auth/me',
        method: 'GET',
      }),
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        try {
          const result = await queryFulfilled
          dispatch(setUser(result.data.data))
        } catch {
          dispatch(clearUser())
        }
      },
      providesTags: ['Auth'],
    }),

    /**
     * POST /api/v1/auth/logout
     * The backend destroys the server-side session.
     * We clear the Redux state; the browser discards the cookie automatically.
     */
    logout: builder.mutation<LogoutResponse, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled
        } finally {
          setCsrfToken(null)
          localStorage.clear()
          sessionStorage.clear()
          dispatch(clearUser())
        }
      },
    }),

    /**
     * POST /api/v1/auth/refresh
     * Rotates session token matrices.
     */
    refresh: builder.mutation<RefreshResponse, void>({
      query: () => ({
        url: '/auth/refresh',
        method: 'POST',
      }),
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        try {
          const result = await queryFulfilled
          dispatch(setUser(result.data.data))
        } catch {
          dispatch(clearUser())
        }
      },
    }),

    /**
     * GET /api/v1/auth/profile (protected)
     * Fetches user profile detail data.
     */
    getProfile: builder.query<ProfileResponse, void>({
      query: () => ({
        url: '/auth/profile',
        method: 'GET',
      }),
      providesTags: ['Auth'],
    }),

    /**
     * GET /api/v1/auth/users/profile/{id}
     * Fetches user profile details by ID.
     */
    getUserProfileById: builder.query<ProfileResponse, number | string>({
      query: (id) => ({
        url: `/auth/users/profile/${id}`,
        method: 'GET',
      }),
      providesTags: ['Auth'],
    }),

    /**
     * PUT /api/v1/auth/profile
     * Updates current user profile information.
     */
    updateUserProfile: builder.mutation<ProfileResponse, UpdateProfileRequest>({
      query: (body) => ({
        url: '/auth/profile',
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: ['Auth'],
    }),

    /**
     * POST /api/v1/auth/forgot-password/initiate
     * Sends OTP code to user's registered email address.
     */
    forgotPasswordInitiate: builder.mutation<ForgotPasswordInitiateResponse, ForgotPasswordInitiateRequest>({
      query: (body) => ({
        url: '/auth/forgot-password/initiate',
        method: 'POST',
        data: body,
      }),
    }),

    /**
     * POST /api/v1/auth/forgot-password/verify-otp
     * Verifies 6-digit OTP and returns resetToken.
     */
    forgotPasswordVerifyOtp: builder.mutation<ForgotPasswordVerifyOtpResponse, ForgotPasswordVerifyOtpRequest>({
      query: (body) => ({
        url: '/auth/forgot-password/verify-otp',
        method: 'POST',
        data: body,
      }),
    }),

    /**
     * POST /api/v1/auth/forgot-password/reset
     * Resets password using email, resetToken, and newPassword.
     */
    forgotPasswordReset: builder.mutation<ForgotPasswordResetResponse, ForgotPasswordResetRequest>({
      query: (body) => ({
        url: '/auth/forgot-password/reset',
        method: 'POST',
        data: body,
      }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useLoginMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
  useLogoutMutation,
  useRefreshMutation,
  useGetProfileQuery,
  useLazyGetProfileQuery,
  useGetUserProfileByIdQuery,
  useLazyGetUserProfileByIdQuery,
  useUpdateUserProfileMutation,
  useForgotPasswordInitiateMutation,
  useForgotPasswordVerifyOtpMutation,
  useForgotPasswordResetMutation,
} = authApi

